export type AccountMinimalResource = { uuid: string; name: string };
export enum AccountOrigin {
  PLG_DOCUMENT = 'plg_document',
  REGISTRATION = 'registration',
  APPLICATION = 'application',
  NOVA = 'nova',
  REGISTER_SHOWCASE_AI_ANALYSIS = 'register_showcase_ai_analysis',
  REGISTER_SHOWCASE_GLOBAL_TEMPLATE = 'register_showcase_global_template',
}
export type AccountPartyMinimalResource = { uuid: string; entity_name: string };
export type AccountPartyResource = { uuid: string; entity_type: string; entity_name: string; address: string };
export type AccountSnippetResource = {
  uuid: string;
  name: string;
  suspended_at: string;
  is_internal: boolean;
  sso_enforced: boolean;
};
export enum AccountUserInvitationStatus {
  ACCEPTED = 'accepted',
  DECLINED = 'declined',
  EXPIRED = 'expired',
  PENDING = 'pending',
  CANCELED = 'canceled',
}
export type AccountUserMinimalResource = {
  uuid: string;
  first_name: string;
  last_name: string;
  profile_photo_url: string;
  deleted_at: string | null;
};
export enum AccountUserPermission {
  ACCOUNT_GRANT_ACCESS = 'account_grant_access',
  ACCOUNT_MANAGE = 'account_manage',
  IMPORT_MANAGE = 'import_manage',
  AUTOMATION_MANAGE = 'automation_manage',
  TEMPLATE_MANAGE = 'template_manage',
  DOCUMENT_ARCHIVE = 'document_archive',
  DOCUMENT_DELETE = 'document_delete',
  DOCUMENT_RESTORE = 'document_restore',
  DOCUMENT_CREATE_FROM_API_INTEGRATION = 'document_create_from_api_integration',
  DOCUMENT_CREATE_FROM_PDF = 'document_create_from_pdf',
  DOCUMENT_CREATE_FROM_SCRATCH = 'document_create_from_scratch',
  DOCUMENT_CREATE_FROM_SIGNED_PDF = 'document_create_from_signed_pdf',
  DOCUMENT_CREATE_FROM_TEMPLATE = 'document_create_from_template',
  DOCUMENT_TAG_MANAGE = 'document_tag_manage',
  DOCUMENT_TEAM_MANAGE = 'document_team_manage',
  DOCUMENT_METADATA_VALUE_MANAGE = 'document_metadata_value_manage',
  DOCUMENT_AI_ANALYSIS_MANAGE = 'document_ai_analysis_manage',
  DOCUMENT_PLAYBOOK_MANAGE = 'document_playbook_manage',
  DOCUMENT_ADD_LINKED_DOCUMENTS = 'document_add_linked_documents',
  DOCUMENT_EXPORT_MANAGE = 'document_export_manage',
  DOCUMENT_SELF_JOIN = 'document_self_join',
  DOCUMENT_USER_MAKE_REVOKE_OWNERSHIP = 'document_user_make_revoke_ownership',
  DOCUMENT_NOTIFICATION_SETTINGS_MANAGE = 'document_notification_settings_manage',
  DOCUMENT_DYNAMIC_FIELD_MANAGE = 'document_dynamic_field_manage',
  DOCUMENT_EDITOR_SESSION_MANAGE = 'document_editor_session_manage',
  DOCUMENT_WORKING_COPY_MANAGE = 'document_working_copy_manage',
  DOCUMENT_COMMENT_RESOLVE_PROPOSAL = 'document_comment_resolve_proposal',
  BULK_AI_ANALYSIS_MANAGE = 'bulk_ai_analysis_manage',
  PLAYBOOK_MANAGE = 'playbook_manage',
  SAML_MANAGE = 'saml_manage',
  SCIM_MANAGE = 'scim_manage',
  METADATA_MANAGE = 'metadata_manage',
  ACCOUNT_USER_MANAGE = 'account_user_manage',
  API_INTEGRATION_MANAGE = 'api_integration_manage',
  CONTRACT_TYPE_MANAGE = 'contract_type_manage',
  DOCUMENT_HEADER_FOOTER_MANAGE = 'document_header_footer_manage',
  EXTERNAL_API_MANAGE = 'external_api_manage',
}
export type AccountUserResource = {
  uuid: string;
  email: string;
  first_name: string;
  last_name: string;
  title: string;
  profile_photo_url: string;
  account: AccountMinimalResource;
  permissions: Array<string>;
  teams: Array<TeamSnippetResource>;
  roles: Array<RoleResource>;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  user: UserResource;
};
export enum AccountUserRole {
  OWNER = 'account-owner',
  ADMIN = 'account-admin',
  EDITOR = 'account-editor',
  MANAGER = 'account-manager',
  LIMITED_MANAGER = 'account-limited-manager',
}
export type AccountUserSnippetResource = {
  uuid: string;
  first_name: string;
  last_name: string;
  title: string;
  profile_photo_url: string;
  deleted_at: string | null;
  teams: Array<TeamSnippetResource>;
};
export type AccountUserWithSsoResource = {
  uuid: string;
  email: string;
  first_name: string;
  last_name: string;
  title: string;
  profile_photo_url: string;
  account: AccountMinimalResource;
  permissions: Array<string>;
  teams: Array<TeamSnippetResource>;
  roles: Array<RoleResource>;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  user: UserResource;
  sso_enabled: boolean;
};
export type AiAnalysisResource = {
  uuid: string;
  status: AiAnalysisStatus;
  document_uuid: string;
  document_name: string;
  document_revision_uuid: string;
  created_at: string;
};
export enum AiAnalysisStatus {
  CREATED = 'created',
  PENDING = 'pending',
  EXECUTING = 'executing',
  REVIEW = 'review',
  REVIEWED = 'reviewed',
  APPLIED = 'applied',
  FAILED = 'failed',
  EXPIRED = 'expired',
  UNPROCESSABLE = 'unprocessable',
  DOCUMENT_DELETED = 'document_deleted',
}
export enum ApiIntegrationErrorType {
  ERROR_INVALID_TYPE = 'invalid_type',
  ERROR_INVALID_DATE = 'invalid_date',
  ERROR_INVALID_FORMAT = 'invalid_format',
  ERROR_INVALID_NUMBER = 'invalid_number',
  ERROR_INVALID_SELECT_VALUE = 'invalid_select_value',
  ERROR_INVALID_VALUE = 'invalid_value',
  ERROR_INVALID_NULL = 'invalid_null',
  ERROR_NO_REFERENCE = 'no_reference',
  ERROR_UNKNOWN = 'unknown',
}
export enum ApiIntegrationObjectSourceKey {
  LABEL = 'label',
  VALUE = 'value',
  OBJECT = 'object',
  REFERENCE_LABEL = 'reference_label',
  REFERENCE_VALUE = 'reference_value',
  REFERENCE_OBJECT = 'reference_object',
}
export type ApiIntegrationSettingMinimalResource = {
  uuid: string;
  template_uuid: string;
  name: string;
  object_name: string;
  api_integration_type: ApiIntegrationType;
};
export enum ApiIntegrationType {
  SALESFORCE = 'salesforce',
  HUBSPOT = 'hubspot',
  PIPEDRIVE = 'pipedrive',
}
export enum ApplicationMimeType {
  PDF = 'application/pdf',
  DOC = 'application/msword',
  DOCX = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  XML = 'application/xml',
  XLS = 'application/vnd.ms-excel',
  XLSX = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  PPT = 'application/vnd.ms-powerpoint',
  PPTX = 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
}
export type AutomationResource = {
  uuid: string;
  teams: Array<TeamMinimalResource>;
  template_uuid: string;
  tags: Array<TagResource>;
  name: string;
  status: AutomationStatus;
  xlsx_url: string | null;
  filename: string | null;
  created_by: CreatedByMinimalResource;
  invitations_sent_at: string | null;
  started_processing_at: string | null;
  finished_processing_at: string | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  archived_at: string | null;
};
export enum AutomationStatus {
  READY_FOR_IMPORT = 'ready_for_import',
  VALIDATED = 'validated',
  SUBMITTED = 'submitted',
  PROCESSING = 'processing',
  FAILED = 'failed',
  PROCESSED = 'processed',
}
export enum AwsClientType {
  TEXTRACT = 'textract',
  BATCH = 'batch',
  STS = 'sts',
  SQS = 'sqs',
}
export enum BillingScheme {
  TIERED = 'tiered',
  PER_UNIT = 'per_unit',
}
export enum BlockedEmailDomainReason {
  TEMPORARY_MAIL = 'temporary_mail',
  MANUAL = 'manual',
}
export enum BookmarkListType {
  DOCUMENT_LIST = 'documentList',
  TEMPLATE_LIST = 'templateList',
}
export enum BroadcastingScope {
  INTERNAL = 'internal',
  EXTERNAL = 'external',
  INTERNAL_AND_EXTERNAL = 'internal_and_external',
}
export enum BulkAiAnalysisStatus {
  PENDING = 'pending',
  EXECUTING = 'executing',
  REVIEW = 'review',
  REVIEWED = 'reviewed',
  APPLIED = 'applied',
  FAILED = 'failed',
}
export enum BulkManageRequestContext {
  TEMPLATE = 'template',
  DOCUMENT = 'document',
}
export enum BulkManageRequestStatus {
  CREATED = 'created',
  IN_PROGRESS = 'in_progress',
  COMPLETED = 'completed',
  FAILED = 'failed',
}
export enum BulkManageRequestType {
  METADATA = 'metadata',
}
export type CardResource = {
  brand: string;
  country: string;
  display_brand: string;
  exp_month: number;
  exp_year: number;
  fingerprint: string;
  funding: string;
  last4: string;
  regulated_status: string;
  three_d_secure_usage: string;
  wallet: string | null;
};
export type CheckoutSessionResource = { url: string };
export enum CheckpointDocumentStage {
  REVIEW = 'review',
  SIGNING = 'signing',
}
export enum CheckpointPassStatus {
  UPCOMING = 'upcoming',
  READY = 'ready',
  ACTIVE = 'active',
  APPROVED = 'approved',
  REJECTED = 'rejected',
  CANCELED = 'canceled',
}
export enum CheckpointPassStepApprovalStatus {
  APPROVED = 'approved',
  REJECTED = 'rejected',
  OPEN = 'open',
}
export enum CheckpointPassStepStatus {
  UPCOMING = 'upcoming',
  ACTIVE = 'active',
  APPROVED = 'approved',
  REJECTED = 'rejected',
  CANCELED = 'canceled',
}
export type CheckpointResource = {
  uuid: string;
  document_stage: CheckpointDocumentStage;
  checkpoint_steps: Array<CheckpointStepResource>;
  created_by: CreatedByMinimalResource;
  created_at: string;
};
export type CheckpointStepApproverResource = {
  uuid: string;
  checkpoint_step_uuid: string;
  account_user: AccountUserMinimalResource;
  created_by: CreatedByMinimalResource;
  created_at: string;
};
export enum CheckpointStepPolicy {
  ONE = 'one',
  TWO = 'two',
  ALL = 'all',
}
export type CheckpointStepResource = {
  uuid: string;
  checkpoint_uuid: string;
  checkpoint_step_approvers: Array<CheckpointStepApproverResource>;
  order: number;
  policy: CheckpointStepPolicy;
  created_by: CreatedByMinimalResource;
  created_at: string;
};
export type CommentResource = {
  uuid: string;
  party_uuid: string;
  comment_uuid: string;
  prosemirror_data_uuid: string;
  type: CommentType;
  proposal_status: ProposalStatus;
  html: string;
  prosemirror_data: Array<any>;
  is_resolved: boolean;
  mentioned_document_user_uuids?: Array<string> | null;
  created_by: CreatedByMinimalResource;
  created_at: string;
  scope: Scope;
};
export enum CommentType {
  COMMENT = 'comment',
  PROPOSAL = 'proposal',
}
export enum CompanySize {
  SIZE_1_9 = '1-9',
  SIZE_10_49 = '10-49',
  SIZE_50_99 = '50-99',
  SIZE_100_249 = '100-249',
  SIZE_250_499 = '250-499',
  SIZE_500_999 = '500-999',
  SIZE_1000_4999 = '1.000-4.999',
  SIZE_5000_9999 = '5.000-9.999',
  SIZE_10000_PLUS = '10.000+',
}
export enum ComparisonOperator {
  EMPTY = 'empty',
  NOT_EMPTY = 'not_empty',
  STARTS_WITH = 'starts_with',
  ENDS_WITH = 'ends_with',
  CONTAINS = 'contains',
  NOT_CONTAINS = 'not_contains',
  EQUAL = 'equal',
  NOT_EQUAL = 'not_equal',
  GREATER_THAN = 'greater_than',
  LESS_THAN = 'less_than',
  GREATER_THAN_OR_EQUAL = 'greater_than_or_equal',
  LESS_THAN_OR_EQUAL = 'less_than_or_equal',
}
export type ContractTypeResource = { uuid: string; name: string; aliases: Array<string> | null };
export enum CookieName {
  TRUST_DEVICE_FOR_2FA = '2fa',
  USER_DEVICE_UUID_PREFIX = 'fynk_device_',
  REFRESH_USER_DEVICE = 'refresh_fynk_device',
}
export type CouponResource = {
  id: string;
  active: boolean;
  code: string;
  percent_off: number;
  expires_at: string | null;
  duration_in_months: number;
};
export type CreateBillingPortalSessionRequest = { type: string };
export type CreateCheckoutSessionRequest = {
  price_id: string;
  quantity: number;
  promotionCodeId: string | undefined;
  currency: 'eur' | 'usd';
};
export type CreateDocumentFromPdfRequest = {
  document_type?: DocumentType;
  pdf_uuid: string;
  pdf_file_name: string;
  name?: string;
  account_party_uuid?: string;
  team_uuids?: Array<string>;
  tag_uuids?: Array<string>;
  template_uuid?: string;
  ai_analysis_auto_apply?: boolean;
};
export type CreateDocumentFromScratchRequest = {
  document_type?: DocumentType;
  name: string;
  account_party_uuid?: string | null;
  team_uuids?: Array<string>;
  tag_uuids?: Array<string>;
  ai_analysis_auto_apply?: boolean;
  initial_content?: any;
};
export type CreateDocumentFromSignedPdfRequest = {
  document_type?: DocumentType;
  pdf_uuid: string;
  pdf_file_name: string;
  name: string;
  account_party_uuid?: string;
  team_uuids?: Array<string>;
  tag_uuids?: Array<string>;
  ai_analysis_auto_apply: boolean;
};
export type CreateSignedStorageUrlRequest = { content_type: string; visibility: 'private' };
export type CreateTemplateFromScratchRequest = {
  document_type: DocumentType;
  name: string;
  account_party_uuid: string;
  locale?: string;
  team_uuids?: Array<string>;
  tag_uuids?: Array<string>;
};
export type CreatedByMinimalResource =
  | ({
      type: 'account_user';
    } & AccountUserMinimalResource)
  | ({
      type: 'document_user';
    } & DocumentUserMinimalResource)
  | ({
      type: 'external_api_actor';
    } & ExternalApiActorMinimalResource);
export enum Currency {
  USD = 'usd',
  EUR = 'eur',
}
export type CustomTaskResource = {
  uuid: string;
  title: string;
  description: string;
  custom_task_type: CustomTaskType;
  subtasks: Array<TaskSnippetResource>;
  taskable_type?: string;
  taskable_uuid?: string;
  taskable_name?: string;
  created_by?: CreatedByMinimalResource;
  resolved_by_account_user?: AccountUserMinimalResource;
  due_at: string;
  resolved_at?: string;
  canceled_at?: string;
  updated_at?: string;
  created_at: string;
};
export enum CustomTaskType {
  REQUIRE_ONE = 'require_one',
  REQUIRE_ALL = 'require_all',
}
export type CustomWidgetResource = {
  uuid: string;
  name: string | null;
  config?: unknown | null;
  dashboard_uuid: DashboardResource['uuid'];
};
export type DashboardResource = {
  uuid: string;
  name: string;
  filter: {
    teams: string[]; // team uuids
    tags: string[]; // tag uuids
    excluded_tags: string[]; // tag uuids
    tag_and: boolean;
    stages: string[];
    contract_type: string[]; // name property of ContractType
    timeframe: number | string | null;
  } | null;
  layout:
    | {
        x: number;
        y: number;
        w: number;
        h: number;
        i: string;
      }[]
    | null;
  dashboardable_uuid: string;
  scope: DashboardScope;
};
export enum DashboardScope {
  ACCOUNT_USER = 'account_user',
  ACCOUNT = 'account',
  TEAM = 'team',
}
export enum DocumentActivityType {
  DOCUMENT_CREATED_FROM_TEMPLATE = 'document_created_from_template',
  DOCUMENT_CREATED_FROM_TEMPLATE_API_INTEGRATION = 'document_created_from_template_api_integration',
  DOCUMENT_CREATED_FROM_SCRATCH = 'document_created_from_scratch',
  DOCUMENT_CREATED_FROM_PDF = 'document_created_from_pdf',
  IMPORTED_SIGNED_PDF = 'imported_signed_pdf',
  DOCUMENT_STAGE_CHANGED = 'document_stage_changed',
  USER_NOTIFIED = 'user_notified',
  WORKING_COPY_CREATED = 'working_copy_created',
  WORKING_COPY_PUBLISHED = 'working_copy_published',
  DOCUMENT_USER_CREATED = 'document_user_created',
  DOCUMENT_USER_CHANGED = 'document_user_changed',
  DOCUMENT_USER_DELETED = 'document_user_deleted',
  PARTY_CREATED = 'party_created',
  PARTY_CHANGED = 'party_changed',
  PARTY_DELETED = 'party_deleted',
  DYNAMIC_FIELD_CREATED = 'dynamic_field_created',
  DYNAMIC_FIELD_CHANGED = 'dynamic_field_changed',
  DYNAMIC_FIELD_DELETED = 'dynamic_field_deleted',
  SIGNATURE_BLOCK_SIGNED = 'signature_block_signed',
  SIGNING_PHASE_CREATED = 'signing_phase_created',
  SIGNING_PHASE_REVERTED = 'signing_phase_reverted',
  COMMENT_CREATED = 'comment_created',
  COMMENT_CHANGED = 'comment_changed',
  COMMENT_RESOLVED = 'comment_resolved',
  ATTACHMENT_CREATED = 'attachment_created',
  ATTACHMENT_UPDATED = 'attachment_updated',
  ATTACHMENT_DELETED = 'attachment_deleted',
  STORED_FILE_CREATED = 'stored_file_created',
  STORED_FILE_UPDATED = 'stored_file_updated',
  STORED_FILE_DELETED = 'stored_file_deleted',
  PROPOSAL_CREATED = 'proposal_created',
  PROPOSAL_ACCEPTED = 'proposal_accepted',
  PROPOSAL_REJECTED = 'proposal_rejected',
  DOCUMENT_PROSEMIRROR_DATA_CHANGED = 'document_prosemirror_data_changed',
  DOCUMENT_HEADER_FOOTER_PROSEMIRROR_DATA_CHANGED = 'document_header_footer_prosemirror_data_changed',
  DOCUMENT_PDF_CHANGED = 'document_pdf_changed',
  DOCUMENT_CHANGED = 'document_changed',
  DOCUMENT_DELETED = 'document_deleted',
  DOCUMENT_RESTORED = 'document_restored',
  DOCUMENT_ARCHIVED = 'document_archived',
}
export enum DocumentCheckpointStatusType {
  OPEN = 'open',
  APPROVED = 'approved',
  REJECTED = 'rejected',
  LOCKED = 'locked',
}
export enum DocumentContentType {
  PROSEMIRROR_DATA = 'prosemirror_data',
  HTML = 'html',
  PDF = 'pdf',
}
export enum DocumentExportDownloadType {
  DIRECT = 'direct',
  ASYNC = 'async',
}
export type DocumentExportResource = { uuid: string; name: string };
export type DocumentExportSnippetResource = {
  uuid: string;
  name: string;
  type: DocumentExportType;
  size: number;
  status: DocumentExportStatus;
  preview: Array<string>;
  created_by: CreatedByMinimalResource;
  created_at: string;
  download_url: string;
};
export enum DocumentExportStatus {
  CREATED = 'created',
  IN_PROGRESS = 'in_progress',
  COMPLETED = 'completed',
  FAILED = 'failed',
}
export enum DocumentExportType {
  ARCHIVE = 'archive',
  METADATA = 'metadata',
}
export type DocumentFilterOptionTemplateMinimalResource = {
  uuid: string;
  name: string;
  document_type: DocumentType;
  teams: Array<TeamMinimalResource>;
  api_integration_settings: Array<ApiIntegrationSettingMinimalResource>;
};
export type DocumentMinimalResource = {
  uuid: string;
  name: string;
  document_type: DocumentType;
  origin: DocumentOrigin;
  stage: DocumentStage;
  updated_at: string;
  created_at: string;
  deleted_at: string | null;
};
export enum DocumentOrigin {
  SIGNED_PDF = 'signed_pdf',
  SIGNED_PDF_EXTERNAL_ACCOUNT = 'signed_pdf_external_account',
  TEMPLATE = 'template',
  SCRATCH = 'scratch',
  PDF = 'pdf',
}
export type DocumentRelationshipResource = {
  uuid: string;
  controller_document: DocumentMinimalResource;
  peripheral_document: DocumentMinimalResource;
  relationship_type: DocumentRelationshipType;
};
export enum DocumentRelationshipType {
  PARENT = 'parent',
  AMENDMENT = 'amendment',
  RENEWAL = 'renewal',
  ADDITION = 'addition',
  REPLACEMENT = 'replacement',
  TERMINATION = 'termination',
}
export type DocumentResource = { uuid: string; name: string };
export type DocumentRevisionHtmlResource = { uuid: string; seal: string; html: string; created_at: string };
export type DocumentRevisionPdfResource = { uuid: string; seal: string; pdf_url: string; created_at: string };
export type DocumentRevisionSnippetResource = {
  uuid: string;
  seal: string;
  created_at: string;
  created_by: CreatedByMinimalResource;
};
export enum DocumentRevisionType {
  PROSEMIRROR_DATA_INIT = 'prosemirror_data_init',
  PROSEMIRROR_DATA_CHANGED = 'prosemirror_data_changed',
  HEADER_FOOTER_PROSEMIRROR_DATA_CHANGED = 'header_footer_prosemirror_data_changed',
  PROSEMIRROR_DATA_WORKING_COPY_PUBLISHED = 'prosemirror_data_working_copy_published',
  PROSEMIRROR_DATA_SIGNING_PHASE_REVERTED = 'prosemirror_data_signing_phase_reverted',
  VARIABLES_CHANGED = 'variables_changed',
  RENDERED_PROSEMIRROR_DATA_SIGNING_PHASE_INIT = 'rendered_prosemirror_data_signing_phase_init',
  RENDERED_PROSEMIRROR_DATA_SIGNATURE_ADDED = 'rendered_prosemirror_data_signature_added',
  PDF_INIT = 'pdf_init',
  EXTERNAL_PDF_INIT = 'external_pdf_init',
  PDF_CHANGED = 'pdf_changed',
  PDF_SIGNATURE_ADDED = 'pdf_signature_added',
  PDF_SIGNING_PHASE_INIT = 'pdf_signing_phase_init',
  PDF_SIGNING_PHASE_REVERTED = 'pdf_signing_phase_reverted',
}
export enum DocumentStage {
  DRAFT = 'draft',
  APPROVED_DRAFT = 'approved_draft',
  REVIEW = 'review',
  APPROVED_REVIEW = 'approved_review',
  SIGNING = 'signing',
  SIGNED = 'signed',
}
export enum DocumentType {
  CONTRACT = 'contract',
  QUOTE = 'quote',
  FORM = 'form',
  OTHER = 'other',
}
export enum DocumentUiHintType {
  DOCUMENT_CREATED = 'document_created',
  REVIEW_READY = 'review_ready',
  REVIEW_ACTIVE = 'review_active',
  SIGNING_READY = 'signing_ready',
  SIGNING_ACTIVE = 'signing_active',
  SIGNED = 'signed',
}
export type DocumentUserMinimalResource = {
  uuid: string;
  first_name: string;
  last_name: string;
  profile_photo_url: string;
  deleted_at: string;
};
export enum DocumentUserPermission {
  DOCUMENT_USER_CREATE = 'document_user_create',
  DOCUMENT_USER_UPDATE = 'document_user_update',
  DOCUMENT_USER_DELETE = 'document_user_delete',
  DOCUMENT_USER_UPDATE_SIGNING_ORDER = 'document_user_update_signing_order',
  DOCUMENT_USER_INVITATION_MANAGE = 'document_user_invitation_manage',
  DYNAMIC_FIELD_UPDATE = 'dynamic_field_update',
  DYNAMIC_FIELD_CREATE = 'dynamic_field_create',
  DYNAMIC_FIELD_DELETE = 'dynamic_field_delete',
  COMMENT_UPDATE = 'comment_update',
  COMMENT_CREATE = 'comment_create',
  COMMENT_DELETE = 'comment_delete',
  CONDITION_UPDATE = 'condition_update',
  CONDITION_CREATE = 'condition_create',
  CONDITION_DELETE = 'condition_delete',
  METADATA_VALUE_UPDATE = 'metadata_value_update',
  METADATA_VALUE_CREATE = 'metadata_value_create',
  METADATA_VALUE_DELETE = 'metadata_value_delete',
  PARTY_UPDATE = 'party_update',
  PARTY_CREATE = 'party_create',
  PARTY_DELETE = 'party_delete',
  DOCUMENT_UPDATE = 'document_update',
  DOCUMENT_DELETE = 'document_delete',
  DOCUMENT_ARCHIVE = 'document_archive',
  DOCUMENT_SIGN = 'document_sign',
  DOCUMENT_SIGNING_VOTE_CREATE = 'document_signing_vote_create',
  DOCUMENT_SIGNING_VOTE_UPDATE = 'document_signing_vote_update',
  DOCUMENT_ACTIVITY_SHOW = 'document_activity_show',
  SIGNING_PHASE_CREATE = 'signing_phase_create',
  PDF_TEXT_CREATE = 'pdf_text_create',
  PDF_TEXT_UPDATE = 'pdf_text_update',
  PDF_TEXT_DELETE = 'pdf_text_delete',
  SIGNATURE_BLOCK_CREATE = 'signature_block_create',
  SIGNATURE_BLOCK_UPDATE = 'signature_block_update',
  SIGNATURE_BLOCK_DELETE = 'signature_block_delete',
  SIGNING_VOTE_BLOCK_CREATE = 'signing_vote_block_create',
  SIGNING_VOTE_BLOCK_UPDATE = 'signing_vote_block_update',
  SIGNING_VOTE_BLOCK_DELETE = 'signing_vote_block_delete',
  START_CHECKPOINT = 'start_checkpoint',
  DOCUMENT_PDF_UPDATE = 'document_pdf_upate',
  IMAGE_CREATE = 'image_create',
  IMAGE_DELETE = 'image_delete',
  ATTACHMENT_CREATE = 'attachment_create',
  ATTACHMENT_UPDATE = 'attachment_update',
  ATTACHMENT_DELETE = 'attachment_delete',
  STORED_FILE_CREATE = 'stored_file_create',
  STORED_FILE_UPDATE = 'stored_file_update',
  STORED_FILE_DELETE = 'stored_file_delete',
  PDF_BRICK_UPDATE = 'pdf_brick_update',
  PDF_BRICK_CREATE = 'pdf_brick_create',
  PDF_BRICK_DELETE = 'pdf_brick_delete',
}
export enum DocumentUserRole {
  OWNER = 'document-owner',
  COLLABORATOR = 'document-collaborator',
  SIGNATORY = 'document-signatory',
}
export type DocumentUserSnippetResource = {
  uuid: string;
  first_name: string;
  last_name: string;
  email: string;
  profile_photo_url: string;
  is_signatory: boolean;
  has_signed: boolean;
  deleted_at: string;
  roles: Array<string>;
  account_user_uuid?: string;
};
export enum DocumentUserTokenType {
  GENERAL = 'general',
}
export enum DocumentUserType {
  INTERNAL = 'internal',
  EXTERNAL = 'external',
}
export enum DocumentVariable {
  VAR_PAGE_NUMBER = '{{ VAR_PAGE_NUMBER }}',
  VAR_REVISION_SEAL = '{{ VAR_REVISION_SEAL }}',
  VAR_REVISION_DATE = '{{ VAR_REVISION_DATE }}',
  VAR_SIGNATURE_SECURITY_LEVEL = '{{ VAR_SIGNATURE_SECURITY_LEVEL }}',
}
export enum DocumentVariableChangeType {
  FIELD_CHANGED = 'field_changed',
  FIELD_ADDED = 'field_added',
  FIELD_REMOVED = 'field_removed',
  NEW_PROSEMIRROR_DATA = 'new_prosemirror_data',
}
export type DocumentVisitResource = { uuid: string; created_by: CreatedByMinimalResource; created_at: string };
export enum DynamicFieldAutofillType {
  DOCUMENT_CREATED_DATE = 'document_created_date',
  DOCUMENT_REVIEW_DATE = 'document_review_date',
  DOCUMENT_SIGNING_DATE = 'document_signing_date',
}
export type DynamicFieldResource = StoreDynamicFieldRequest & {
  uuid: string;
  ref_uuid: string;
  order: number | null;
};
export enum EmailDeliveryStatus {
  DELIVERY = 'delivery',
  BOUNCED = 'bounce',
  COMPLAINT = 'complaint',
  OPEN = 'open',
  PENDING = 'pending',
}
export type EmailLogResource = {
  uuid: string;
  subject: string;
  delivery_status: EmailDeliveryStatus;
  delivery_info: string;
};
export type ExternalApiActorMinimalResource = {
  uuid: string;
  name: string;
  first_name: string;
  last_name: string;
  profile_photo_url: string;
  deleted_at: string | null;
};
export enum ExternalApiActorPermission {
  DOCUMENT_CREATE_FROM_TEMPLATE = 'document_create_from_template',
  DOCUMENT_VIEW_ANY = 'document_view_any',
  DOCUMENT_VIEW = 'document_view',
  DOCUMENT_UPDATE = 'document_update',
  DOCUMENT_REVISION_VIEW = 'document_revision_view',
  DOCUMENT_USER_CREATE = 'document_user_create',
  DOCUMENT_USER_VIEW_ANY = 'document_user_view_any',
  DOCUMENT_USER_UPDATE = 'document_user_update',
  DOCUMENT_USER_DELETE = 'document_user_delete',
  DYNAMIC_FIELD_VIEW_ANY = 'dynamic_field_view_any',
  DYNAMIC_FIELD_UPDATE = 'dynamic_field_update',
  METADATA_VIEW_ANY = 'metadata_view_any',
  METADATA_VALUE_CREATE = 'metadata_value_create',
  METADATA_VALUE_VIEW_ANY = 'metadata_value_view_any',
  METADATA_VALUE_UPDATE = 'metadata_value_update',
  METADATA_VALUE_DELETE = 'metadata_value_delete',
  PARTY_VIEW_ANY = 'party_view_any',
  PARTY_UPDATE = 'party_update',
  TAG_VIEW_ANY = 'tag_view_any',
  TEMPLATE_VIEW_ANY = 'template_view_any',
  TEMPLATE_VIEW = 'template_view',
}
export type ExternalApiActorResource = {
  uuid: string;
  name: string;
  roles: Array<ExternalApiActorRole>;
  expires_at: string | null;
  created_at: string;
  updated_at: string;
};
export enum ExternalApiActorRole {
  ACCOUNT_DOCUMENT_READER = 'account-document-reader',
  ACCOUNT_DOCUMENT_MANAGER = 'account-document-manager',
  ACCOUNT_TEMPLATE_READER = 'account-template-reader',
}
export type ExternalApiSettingResource = {
  uuid: string;
  locked_api_version: string | null;
  current_api_version: string;
  changelog: object;
  created_at: string;
  updated_at: string;
};
export enum Feature {
  DOCUMENTS = '10_documents',
  ACCOUNT_USERS = '20_account-users',
  QES_SIGNATURES = '30_qes-signatures',
  AI_EXTRACTION = '40_ai-extraction',
  CREATE_TEMPLATE = '50_create-template',
  CREATE_DOCUMENT_FROM_TEMPLATE = '51_create-document-from-template',
  CREATE_DOCUMENT_FROM_SCRATCH = '52_create-document-from-scratch',
  REMINDER = '53_reminder',
  CALENDAR = '54_calendar',
  AUTOMATIONS = '55_automations',
  IMPORT_COUNT = '56_import-count',
  ADVANCED_BRANDING = '60_advanced-branding',
  MULTIPLE_ACCOUNT_PARTIES = '70_multiple-account-parties',
  SSO = '80_sso',
  API_INTEGRATIONS = '90_api-integrations',
  EXTERNAL_API = '91_external-api',
  HEADERS_AND_FOOTERS = '95_headers-and-footers',
}
export type FeatureResource = {
  [Feature.DOCUMENTS]: number;
  [Feature.ACCOUNT_USERS]: number;
  [Feature.QES_SIGNATURES]: boolean;
  [Feature.AI_EXTRACTION]: number;
  [Feature.CREATE_TEMPLATE]: boolean;
  [Feature.CREATE_DOCUMENT_FROM_TEMPLATE]: boolean;
  [Feature.CREATE_DOCUMENT_FROM_SCRATCH]: boolean;
  [Feature.REMINDER]: boolean;
  [Feature.CALENDAR]: boolean;
  [Feature.AUTOMATIONS]: boolean;
  [Feature.IMPORT_COUNT]: number;
  [Feature.ADVANCED_BRANDING]: boolean;
  [Feature.MULTIPLE_ACCOUNT_PARTIES]: boolean;
  [Feature.SSO]: boolean;
  [Feature.API_INTEGRATIONS]: boolean;
  [Feature.EXTERNAL_API]: boolean;
  [Feature.HEADERS_AND_FOOTERS]: boolean;
};
export type GlobalTemplateResource = {
  uuid: string;
  name: string;
  locale: string;
  document_type: DocumentType;
  description: string;
  global_template_author_name: string;
  global_template_attribution_url: string;
  global_template_license_name: string;
  global_template_license_url: string;
  created_by_account_user?: { first_name: string; last_name: string };
  created_by_account: { name: string };
  html: string;
};
export enum HttpHeader {
  FYNK_API_VERSION = 'Fynk-Api-Version',
}
export enum ImageMimeType {
  JPEG = 'image/jpeg',
  PNG = 'image/png',
  WEBP = 'image/webp',
  GIF = 'image/gif',
}
export type ImageResource = { uuid: string; ref_uuid: string; image_data: string };
export enum ImportAiAnalysisApplyStatus {
  PENDING = 'pending',
  APPLYING = 'applying',
  COMPLETED = 'completed',
}
export type ImportMinimalResource = { uuid: string; name: string };
export type ImportResource = {
  uuid: string;
  teams: Array<TeamSnippetResource>;
  tags: Array<TagResource>;
  name: string;
  status: ImportStatus;
  files: Array<string>;
  type: ImportType;
  template: TemplateMinimalResource;
  account_party: AccountPartyMinimalResource;
  created_by: CreatedByMinimalResource;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
};
export type ImportSnippetResource = {
  uuid: string;
  team_uuids: Array<string>;
  tags: Array<TagResource>;
  name: string;
  status: ImportStatus;
  files: Array<string>;
  created_by: CreatedByMinimalResource;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  archived_at: string | null;
};
export enum ImportStatus {
  CREATED = 'created',
  DOCUMENTS_CREATED = 'documents_created',
  AI_ANALYSES_REVIEW = 'ai_analyses_review',
  AI_ANALYSES_APPLIED = 'ai_analyses_applied',
}
export enum ImportType {
  SIGNED_PDF = 'signed_pdf',
  PDF = 'pdf',
}
export enum Industry {
  AGRICULTURE = 'agriculture',
  ARTS = 'arts',
  CONSTRUCTION = 'construction',
  CORPORATE_SERVICES = 'corporate_services',
  CONSUMER_GOODS = 'consumer_goods',
  DESIGN = 'design',
  EDUCATION = 'education',
  ENERGY_AND_MINING = 'energy_and_mining',
  ENTERTAINMENT = 'entertainment',
  FINANCE = 'finance',
  HEALTHCARE = 'healthcare',
  HARDWARE_AND_NETWORKING = 'hardware_and_networking',
  LEGAL = 'legal',
  MANUFACTURING = 'manufacturing',
  MEDIA_AND_COMMUNICATIONS = 'media_and_communications',
  NON_PROFIT = 'nonprofit',
  PUBLIC_ADMINISTRATION = 'public_administration',
  PUBLIC_SAFETY = 'public_safety',
  REAL_ESTATE = 'real_estate',
  RECREATION_AND_TRAVEL = 'recreation_and_travel',
  RETAIL = 'retail',
  SOFTWARE_AND_IT_SERVICES = 'software_and_it_services',
  TRANSPORTATION_AND_LOGISTICS = 'transportation_and_logistics',
  WELLNESS_AND_FITNESS = 'wellness_and_fitness',
}
export enum Interval {
  YEAR = 'year',
  MONTH = 'month',
}
export enum LeadType {
  CONTACT_FORM = 'contact_form',
  DEMO_FORM = 'demo_form',
  PARTNERSHIP_FORM = 'partnership_form',
  REGISTRATION = 'registration',
  REGISTER_SHOWCASE_AI_ANALYSIS = 'register_showcase_ai_analysis',
  REGISTER_SHOWCASE_GLOBAL_TEMPLATE = 'register_showcase_global_template',
  PLG_DOCUMENT = 'plg_document',
  APPLICATION = 'application',
  NOVA = 'nova',
}
export enum MailcoachTag {
  CUSTOMER = 'customer',
  LOCALE_DE = 'de',
  LOCALE_EN = 'en',
  USER = 'user',
  OWNER = 'owner',
  ADMIN = 'admin',
  MANAGER = 'manager',
  EDITOR = 'editor',
  NEWSLETTER = 'newsletter',
  WELCOME_ACCOUNT = 'welcome-account',
  WELCOME_USER = 'welcome-user',
}
export type MauResource = {
  uuid: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  title: string | null;
  mobile_phone: string | null;
  profile_photo_url: string;
  customizations: {
    pinnedFilters?: {
      documentList?: string[];
      templateList?: string[];
    };
    tables?: {
      userList?: string[];
      automationList?: string[];
      documentList?: string[];
      importList?: string[];
      templateList?: string[];
      dashboardWidgetDocumentList?: string[];
      aiAnalysisList?: string[];
      ledgerTable?: string[];
      partyList?: string[];
      teamList?: string[];
      tagList?: string[];
      documentHeaderFooterList?: string[];
      playbookList?: string[];
      documentEventSettingList?: string[];
      documentExportList?: string[];
      contractTypeList?: string[];
      metadataList?: string[];
      apiTokenList?: string[];
    };
  } | null;
  permissions: AccountUserPermission[];
  teams: TeamSnippetResource[];
  roles: RoleResource[];
  account_uuid: string;
  dashboard_uuid: string | null;
  created_at: string;
  deleted_at: string | null;
  suspended_at: string | null;
  user: MuResource;
};
export enum MessageMimeType {
  MSG = 'message/rfc822',
}
export type MetadataMinimalResource = {
  uuid: string;
  type: MetadataType;
  value_type: MultiFieldType;
  account_metadata_uuid: string;
  name: string;
  display_name: string;
  select_values: null | Array<string>;
};
export enum MetadataType {
  SYSTEM_AUTOFILLED = 'system_autofilled',
  SYSTEM_COMPUTED = 'system_computed',
  SYSTEM = 'system',
  SYSTEM_REFERENCE = 'system_reference',
  ACCOUNT = 'account',
}
export enum MetadataValueComputedStatus {
  MISSING_INFORMATION = 'missing_information',
  CONFLICTING_INFORMATION = 'conflicting_information',
  NOT_APPLICABLE = 'not_applicable',
  COMPUTED = 'computed',
}
export enum MetadataValueSource {
  USER = 'user',
  AI_ANALYSIS = 'ai_analysis',
  COMPUTED = 'computed',
  AUTOFILLED = 'autofilled',
  DYNAMIC_FIELD = 'dynamic_field',
}
export type MuResource = {
  uuid: string;
  email: string;
  locale: string;
  roles: Array<RoleResource>;
  email_verified_at: string;
  has_password: boolean;
};
export enum MultiFieldType {
  ID = 'id',
  BOOL = 'bool',
  DATE = 'date',
  DURATION = 'duration',
  EMAIL = 'email',
  LIST = 'list',
  NUMBER = 'number',
  CURRENCY = 'currency',
  CURRENCY_DURATION = 'currency_duration',
  CLAUSE = 'clause',
  SELECT = 'select',
  TEXT = 'text',
  TEXTAREA = 'textarea',
  TIMESTAMP = 'timestamp',
  ARRAY = 'array',
  ENUM = 'enum',
}
export type NewExternalApiActorResource = {
  uuid: string;
  name: string;
  roles: Array<ExternalApiActorRole>;
  token: string;
  expires_at: string | null;
  created_at: string;
  updated_at: string;
};
export enum NotificationChannel {
  MAIL = 'mail',
  DATABASE = 'database',
}
export type NotificationResource = {
  uuid: string;
  data: {
    document_uuid?: string;
    comment_uuid?: string;
    import_uuid?: string;
    rejected_by_name?: string;
    rejected_by_email?: string;
    context?: BulkManageRequestContext;
  };
  type: NotificationType;
  read_at?: string;
  created_at: string;
  created_by?: CreatedByMinimalResource;
  document?: DocumentResource;
  document_export?: DocumentExportResource;
  import?: ImportMinimalResource;
  party?: PartyResource;
  custom_task?: CustomTaskResource;
};
export enum NotificationType {
  ACCOUNT_ACTIVATED = 'account_activated',
  CHECKPOINT_APPROVAL_REQUIRED = 'checkpoint_approval_required',
  COMMENT_CREATED = 'comment_created',
  COMMENT_MENTION = 'comment_mention',
  COMMENT_PROPOSAL_REJECTED = 'comment_proposal_rejected',
  COMMENT_REPLIED = 'comment_replied',
  DOCUMENT_EFFECTIVE_REMINDER = 'document_effective_reminder',
  DOCUMENT_EXPIRING_REMINDER = 'document_expiring_reminder',
  DOCUMENT_FULLY_SIGNED = 'document_fully_signed',
  DOCUMENT_INVITED = 'document_invited',
  DOCUMENT_INVITED_CC = 'document_invited_cc',
  DOCUMENT_MOVED_TO_SIGNING_PHASE = 'document_moved_to_signing_phase',
  DOCUMENT_CANCELLATION_NOTICE_PERIOD_REMINDER = 'document_cancellation_notice_period_reminder',
  DOCUMENT_RENEWAL_REMINDER = 'document_renewal_reminder',
  DOCUMENT_SIGNING_PHASE_REVERTED = 'document_signing_phase_reverted',
  DOCUMENT_EXPORT_COMPLETED = 'document_export_completed',
  PARTY_READY_FOR_SIGNING = 'party_ready_for_signing',
  SIGNATURE_REQUIRED = 'signature_required',
  SIGNATURE_REQUIRED_RECURRING = 'signature_required_recurring',
  IMPORT_STATUS_AI_ANALYSES_REVIEW = 'import_status_ai_analyses_review',
  TRIAL_ENDING = 'trial_ending',
  TRIAL_EXPIRED = 'trial_expired',
  CUSTOM_TASK = 'custom_task',
  CUSTOM_TASK_RESOLVED = 'custom_task_resolved',
  BULK_MANAGE_METADATA_COMPLETED = 'bulk_manage_metadata_completed',
}
export enum OcrRequestStatus {
  CREATED = 'created',
  EXECUTING = 'executing',
  FINISHED = 'finished',
  FAILED = 'failed',
  DOCUMENT_DELETED = 'document_deleted',
}
export enum PartyEntityType {
  BUSINESS = 'business',
  PERSON = 'person',
}
export type PartyResource = { uuid: string; name: string; scope: string; entity_type: PartyEntityType };
export type PartySnippetResource = {
  uuid: string;
  name: string;
  entity_name: string;
  address: string;
  scope: Scope;
  is_ready_for_signing: boolean;
  ready_for_signing_at: string;
  entity_type: PartyEntityType;
  created_at: string;
  updated_at: string;
};
export enum PdfBrickableType {
  SIGNATURE_BLOCK = 'App\\Models\\SignatureBlock',
  SIGNING_VOTE_BLOCK = 'App\\Models\\SigningVoteBlock',
  PARTY = 'App\\Models\\Party',
  PDF_TEXT = 'App\\Models\\PdfText',
  COMMENT = 'App\\Models\\Comment',
  DYNAMIC_FIELD = 'App\\Models\\DynamicField',
}
export enum PipedriveObjectKey {
  ACTIVITIES = 'activities',
  DEALS = 'deals',
  ORGANIZATIONS = 'organizations',
  PERSONS = 'persons',
  PRODUCTS = 'products',
}
export type PlanResource = { amount: number; interval: string; nickname: string; product: ProductResource };
export enum PlaybookCheckConditionResult {
  FULFILLED = 'fulfilled',
  NOT_FULFILLED = 'not_fulfilled',
}
export enum PlaybookCheckFlag {
  RED = 'red',
  ORANGE = 'orange',
  GREEN = 'green',
}
export enum PlaybookCheckStatus {
  CREATED = 'created',
  PENDING = 'pending',
  EXECUTING = 'executing',
  COMPLETED = 'completed',
  FAILED = 'failed',
  INVALIDATED = 'invalidated',
  DOCUMENT_DELETED = 'document_deleted',
}
export enum PlaybookConditionImplication {
  RED_FLAG = 'red_flag',
  ORANGE_FLAG = 'orange_flag',
}
export enum PlaybookConditionType {
  METADATA_CONDITION = 'metadata_condition',
  CLAUSE_PRESENT = 'clause_present',
  CLAUSE_NOT_PRESENT = 'clause_not_present',
  PROMPT = 'prompt',
}
export type PreemptiveSignatureResource = {
  uuid: string;
  signature_block_uuid: string;
  document_user_uuid: string;
  signature_seal: any;
  created_at: string;
};
export type PriceResource = {
  id: string;
  currency: 'eur' | 'usd';
  recurring: {
    interval: 'year' | 'month';
  };
  unit_amount: number;
};
export enum PricingModel {
  YEAR_2024 = '2024',
  YEAR_2025 = '2025',
}
export type ProductResource = { id: string; features: FeatureResource; name: string; prices: Array<PriceResource> };
export enum ProposalStatus {
  OPEN = 'open',
  ACCEPTED = 'accepted',
  REJECTED = 'rejected',
}
export enum RequestAttribute {
  ACTIVE_EXTERNAL_API_VERSION = 'active_external_api_version',
  BROADCAST_EVENTS_AFTER_COMMIT = 'broadcast_events_after_commit',
  BROADCAST_EVENTS_TO_OTHERS_AFTER_COMMIT = 'broadcast_events_to_others_after_commit',
  BROADCAST_TRANSACTION_LEVEL = 'broadcast_transaction_level',
  CURRENT_USER_DEVICE_UUID = 'current_user_device_uuid',
  IS_SAML_LOGIN = 'is_saml_login',
  TWO_FACTOR_AUTH_TRUST_DEVICE_ACTION = 'two_factor_auth_trust_device_action',
}
export type RetrieveUpcomingInvoiceRequest = { quantity: number };
export type RoleResource = { uuid: string; name: AccountUserRole; display_name: string };
export enum Scope {
  INTERNAL = 'internal',
  INTERNAL_AND_EXTERNAL = 'internal_and_external',
}
export type SessionResource = {
  id: string;
  ip_address: string;
  browser_platform: string | null;
  browser_name: string | null;
  browser_version: string | null;
  is_active: boolean;
  last_activity: string;
};
export type SignatureBlockResource = {
  uuid: string;
  ref_uuid: string;
  party_uuid: string;
  style: SignatureBlockStyle;
  deleted_at: string | null;
};
export enum SignatureBlockStyle {
  REGULAR = 'regular',
  MINIMAL = 'minimal',
}
export type SignatureResource = {
  uuid: string;
  signing_phase_uuid: string;
  signature_block_uuid: string | null;
  signature_security_level: SignatureSecurityLevel;
  signature_seal: any;
  signatory_name: string;
  signatory_email: string;
  signatory_mobile_phone: string | null;
  secure_id: string;
  created_by: CreatedByMinimalResource;
  created_at: string;
};
export enum SignatureSealType {
  DRAW = 'draw',
  TYPE = 'type',
  PHOTO = 'photo',
}
export enum SignatureSecurityLevel {
  AES = 'aes',
  QES = 'qes',
  SES = 'ses',
}
export enum SigningVoteBlockType {
  YES_NO = 'yes_no',
  YES_NO_ABSTAIN = 'yes_no_abstain',
}
export type SignupResource = { uuid: string; type: SignupType; pdf_file_name: string; global_template_uuid: string };
export enum SignupType {
  REGISTRATION = 'registration',
  REGISTER_SHOWCASE_AI_ANALYSIS = 'register_showcase_ai_analysis',
  REGISTER_SHOWCASE_GLOBAL_TEMPLATE = 'register_showcase_global_template',
  PLG_DOCUMENT = 'plg_document',
  APPLICATION = 'application',
  NOVA = 'nova',
}
export type StoreCheckpointStepRequest = { order: number; policy: CheckpointStepPolicy };
export type StoreCustomWidgetRequest = {
  dashboard_uuid: DashboardResource['uuid'];
  name?: string | null;
  config?: CustomWidgetResource['config'];
};
export type StoreDashboardRequest = {
  filter?: DashboardResource['filter'] | null;
  layout?: DashboardResource['layout'] | null;
  name: DashboardResource['name'];
  dashboardable_uuid: DashboardResource['dashboardable_uuid'];
  scope: DashboardResource['scope'];
  is_default?: boolean;
};
export type StoreDynamicFieldRequest = {
  type: MultiFieldType;
  scope: Scope;
  name: string;
  select_values?: string[] | null;
  is_mandatory: boolean;
  question?: string | null;
  question_external?: string | null;
  settings?: {
    currencies: string[];
  } | null;
  autofill_type: DynamicFieldAutofillType | null;
  format?: {
    decimals: string;
    decimalSeparator: string;
    thousandsSeparator: string;
  } | null;
  value: boolean | number | string | null;
};
export type StoreExternalApiActorRequest = {
  name: string;
  roles: Array<ExternalApiActorRole>;
  expires_at?: string | null;
};
export enum SubIndustry {
  EDUCATION_MANAGEMENT = 'education_management',
  ELEARNING = 'e_learning',
  HIGHER_EDUCATION = 'higher_education',
  PRIMARY_SECONDARY_EDUCATION = 'primary_secondary_education',
  RESEARCH = 'research',
  BUILDING_MATERIALS = 'building_materials',
  CIVIL_ENGINEERING = 'civil_engineering',
  CONSTRUCTION = 'construction',
  ARCHITECTURE_PLANNING = 'architecture_planning',
  DESIGN = 'design',
  GRAPHIC_DESIGN = 'graphic_design',
  ACCOUNTING = 'accounting',
  BUSINESS_SUPPLIES_EQUIPMENT = 'busines_supplies_equiment',
  ENVIRONMENTAL_SERVICES = 'environmental_services',
  EVENTS_SERVICES = 'events_services',
  EXECUTIVE_OFFICE = 'executive_office',
  FACILITIES_SERVICES = 'facilities_services',
  MANAGEMENT_CONSULTING = 'management_consulting',
  OUTSOURCING_OFFSHORING = 'outsourcing_offshoring',
  PROFESSIONAL_TRANING_COACHING = 'professional_training_coaching',
  SECURITY_INVESTIGATIONS = 'security_investigations',
  STAFFING = 'staffing_recruiting',
  RETAIL = 'retail',
  SUPERMARKET = 'supermarket',
  WHOLESALE = 'wholesale',
  OIL_ENERGY = 'oil_energy',
  MINING_METALS = 'mining_metals',
  UTILITIES = 'utilities',
  AUTOMOTIVE = 'automotive',
  AVIATION_AEROSPACE = 'aviation_aerospace',
  CHEMICALS = 'chemicals',
  DEFENSE_SPACE = 'defense_space',
  ELECTRICAL_ELECTRONIC_MANUFACTURING = 'electrical_electronic_manufacturing',
  FOOD_PRODUCTION = 'food_production',
  GLASS_CHERAMICS_CONRECTE = 'glass_cheramics_concrete',
  INDUSTRIAL_AUTOMATION = 'industrial_automation',
  MACHINERY = 'machinery',
  MECHANICAL_INDUSTRIAL_ENGINEERING = 'mechanical_industrial_engineering',
  PACKAGING_CONTAINERS = 'packaging_containers',
  PAPER_FOREST_PRODUCTS = 'paper_forest_products',
  PLASTICS = 'plastics',
  RAILROAD_MANUFACTURE = 'railroad_manufacture',
  RENEWABLES_ENVIRONMENT = 'renewables_environment',
  SHIPBUILDING = 'shipbuilding',
  TEXTILES = 'textiles',
  BANKING = 'banking',
  CAPITAL_MARKETS = 'capital_markets',
  FINANCIAL_SERVICES = 'financial_services',
  INSURANCE = 'insurance',
  INVESTMENT_BANKING = 'investent_banking',
  INVESTMENT_MANAGEMENT = 'investment_management',
  VENTURE_CAPITAL_PRIVATE_EQUITY = 'venture_capital_private_equity',
  AIRLINES_AVIATION = 'airlines_aviation',
  GAMBLING_CASINOS = 'gambling_casinos',
  HOSPITALITY = 'hospitality',
  LEISURE_TRAVEL_TOURISM = 'leisure_travel_tourism',
  RESTAURANTS = 'restaurants',
  RECREATIONAL_FACILITIES_SERVICES = 'recreational_facilities_services',
  SPORTS = 'sports',
  ARTS_CRAFTS = 'arts_crafts',
  FINE_ART = 'fine_art',
  PERFORMING_ARTS = 'performing_arts',
  PHOTOGRAPHY = 'photography',
  BIOTECHNOLOGY = 'biotechnology',
  HOSPITAL_HEALTH_CARE = 'hospital_health_care',
  MEDICAL_DEVICE = 'medical_device',
  MEDICAL_PRACTICE = 'medical_practice',
  MENTAL_HEALTH_CARE = 'mental_health_care',
  PHARMACEUTICALS = 'pharmaceuticals',
  VETERINARY = 'veterinary',
  COMPUTER_HARDWARE = 'computer_hardware',
  COMPUTER_NETWORKING = 'computer_networking',
  NANOTECHNOLOGY = 'nanotechnology',
  SEMICONDUCTORS = 'semiconductors',
  TELECOMMUNICATIONS = 'telecommunications',
  WIRELESS = 'wireless',
  COMMERCIAL_REAL_ESTATE = 'commercial_real_estate',
  REAL_ESTATE = 'real_estate',
  ALTERNATIVE_DISPUTE_RESOLUTION = 'alternative_dispute_resolution',
  LAW_PRACTICE = 'law_practice',
  LEGAL_SERVICES = 'legal_services',
  APPAREL_FASHION = 'apparel_fashion',
  CONSUMER_ELECTRONICS = 'consumer_electronics',
  CONSUMER_GOODS = 'consumer_goods',
  CONSUMER_SERVICES = 'consumer_services',
  COSMETICS = 'cosmetics',
  FOOD_BEVERAGES = 'food_beverages',
  FURNITURE = 'furniture',
  LUXURY_GOODS_JEWELRY = 'luxury_goods_jewelry',
  SPORTING_GOODS = 'sporting_goods',
  TOBACCO = 'tobacco',
  WINE_AND_SPIRITS = 'wine_and_spirits',
  DAIRY = 'dairy',
  FARMING = 'farming',
  FISHERY = 'fishery',
  RANCHING = 'ranching',
  MARKET_RESEARCH = 'market_research',
  MARKETING_ADVERTISING = 'marketing_advertising',
  NEWSPAPERS = 'newspapers',
  ONLINE_MEDIA = 'online_media',
  PRINTING = 'printing',
  PUBLIC_RELATIONS_COMMUNICATIONS = 'public_relations_communications',
  PUBLISHING = 'publishing',
  TRANSLATION_LOCALIZATION = 'translation_localization',
  WRITING_EDITING = 'writing_editing',
  CIVIC_SOCIAL_ORGANIZATION = 'civic_social_organization',
  FUNDRAISING = 'fundraising',
  INDIVIDUAL_FAMILY_SERVICES = 'individual_family_services',
  INTERNATIONAL_TRADE_DEVELOPMENT = 'international_trade_development',
  LIBRARIES = 'libraries',
  MUSEUMS_INSTITUTIONS = 'museums_institutions',
  NON_PROFIT_ORGANIZATION_MANAGEMENT = 'non_profit_organization_management',
  PHILANTROPY = 'philantropy',
  PROGRAM_DEVELOPMENT = 'program_development',
  RELIGIOUS_INSTITUTIONS = 'religious_institutions',
  THINK_TANKS = 'think_tanks',
  COMPUTER_NETWORK_SECURITY = 'computer_network_security',
  COMPUTER_SOFTWARE = 'computer_software',
  INFORMATION_TECHNOLOGY_SERVICES = 'information_technology_services',
  INTERNET = 'internet',
  IMPORT_EXPORT = 'import_export',
  LOGISTICS_SUPPLY_CHAIN = 'logistics_supply_chain',
  MARITIME = 'maritime',
  PACKAGE_FREIGHT_DELIVERY = 'package_freight_delivery',
  TRANSPORTATION_TRUCKING_RAILROAD = 'transportation_trucking_railroad',
  WAREHOUSING = 'warehousing',
  ANIMATION = 'animation',
  BROADCAST_MEDIA = 'broadcast_media',
  COMPUTER_GAMES = 'computer_games',
  ENTERTAINMENT = 'entertainment',
  MEDIA_PRODUCTION = 'media_production',
  MOBILE_GAMES = 'mobile_games',
  MOTION_PICTURES_FILMS = 'motion_pictures_films',
  MUSIC = 'music',
  ALTERNATIVE_MEDICINE = 'alternative_medicine',
  HEALTH_WELLNESS_FITNESS = 'health_wellness_fitness',
  LAW_ENFORCEMENT = 'law_enforcement',
  MILITARY = 'military',
  PUBLIC_SAFETY = 'public_safety',
  GOVERNMENT_ADMINISTRATION = 'government_administration',
  GOVERNMENT_RELATIONS = 'government_relations',
  INTERNATIONAL_AFFAIRS = 'international_affairs',
  JUDICIARY = 'judiciary',
  LEGISLATIVE_OFFICE = 'legislative_office',
  POLITICAL_ORGANIZATION = 'political_organization',
  PUBLIC_POLICY = 'public_policy',
}
export type SubscriptionResource = {
  cancel_at: number | null;
  currency: 'eur' | 'usd';
  current_period_end: string;
  current_period_start: string;
  plan: {
    amount: number;
    interval: 'year' | 'month';
    nickname: string;
    price_id: string;
    product: {
      id: string;
      name: string;
      pricing: string;
    };
    features: FeatureResource | null;
  };
  object: object;
  schedule: string | null;
  status: SubscriptionStatus;
};
export enum SubscriptionScheduleInternalStatus {
  INACTIVE = 'inactive',
  UPCOMING_SUBSCRIPTION_CREATION = 'upcoming_subscription_creation',
  UPCOMING_DOWNGRADE = 'upcoming_downgrade',
  UPCOMING_UPGRADE = 'upcoming_upgrade',
  UPCOMING_BILLING_INTERVAL_CHANGE = 'upcoming_billing_interval_change',
  DONE = 'done',
}
export enum SubscriptionScheduleStatus {
  NOT_STARTED = 'not_started',
  ACTIVE = 'active',
  COMPLETED = 'completed',
  RELEASED = 'released',
  CANCELED = 'canceled',
}
export enum SubscriptionStatus {
  FREE = 'free',
  INCOMPLETE = 'incomplete',
  INTERNAL = 'internal',
  PAID = 'paid',
  SUSPENDED = 'suspended',
  TRIAL = 'trial',
}
export enum SuperadminAccessLogType {
  SUPERADMIN_VIEW = 'superadmin_view',
  SUPERADMIN_EDIT = 'superadmin_edit',
  SUPERADMIN_DELETE = 'superadmin_delete',
  SUPERADMIN_ACTION = 'superadmin_action',
}
export enum SuspensionAction {
  SUSPEND = 'suspend',
  UNSUSPEND = 'unsuspend',
}
export enum SystemContractType {
  DATA_PROCESSING_AGREEMENT = 'data_processing_agreement',
  DEED_OF_TRUST = 'deed_of_trust',
  EMPLOYMENT_CONTRACT = 'employment_contract',
  INVESTMENT_AGREEMENT = 'investment_agreement',
  LETTER_OF_INTENT = 'letter_of_intent',
  LICENSE_AGREEMENT = 'license_agreement',
  LOAN_AGREEMENT = 'loan_agreement',
  NON_DISCLOSURE_AGREEMENT = 'non_disclosure_agreement',
  PARTNERSHIP_AGREEMENT = 'partnership_agreement',
  PRIVACY_POLICY = 'privacy_policy',
  PURCHASE_AGREEMENT = 'purchase_agreement',
  RENTAL_AGREEMENT = 'rental_agreement',
  SERVICE_AGREEMENT = 'service_agreement',
  TERMS_AND_CONDITIONS = 'terms_and_conditions',
}
export enum SystemMetadataType {
  CREATED_DATE = 'created_date',
  DRAFT_DURATION = 'draft_duration',
  REVIEW_DATE = 'review_date',
  REVIEW_DURATION = 'review_duration',
  SIGNING_DATE = 'signing_date',
  SIGNING_DURATION = 'signing_duration',
  SIGNED_DATE = 'signed_date',
  CHANGE_OF_CONTROL_CLAUSE = 'change_of_control_clause',
  FORCE_MAJEURE_CLAUSE = 'force_majeure_clause',
  INDEMNIFICATION_CLAUSE = 'indemnification_clause',
  INSURANCE_CLAUSE = 'insurance_clause',
  LIMITATION_OF_LIABILITY_CLAUSE = 'limitation_of_liability_clause',
  NON_COMPETE_CLAUSE = 'non_compete_clause',
  AUDIT_RIGHTS_CLAUSE = 'audit_rights_clause',
  CONFIDENTIALITY_CLAUSE = 'confidentiality_clause',
  DISPUTE_RESOLUTION_CLAUSE = 'dispute_resolution_clause',
  GOVERNING_LAW_CLAUSE = 'governing_law_clause',
  INTELLECTUAL_PROPERTY_CLAUSE = 'intellectual_property_clause',
  SEVERABILITY_CLAUSE = 'severability_clause',
  DATA_BREACH_CLAUSE = 'data_breach_clause',
  EXCLUSIVITY_CLAUSE = 'exclusivity_clause',
  NON_SOLICITATION_CLAUSE = 'non_solicitation_clause',
  PUBLICITY_CLAUSE = 'publicity_clause',
  SERVICE_LEVEL_AGREEMENT_CLAUSE = 'service_level_agreement_clause',
  TERMINATION_CLAUSE = 'termination_clause',
  WARRANTY_CLAUSE = 'warranty_clause',
  ASSIGNMENT_CLAUSE = 'assignment_clause',
  ENTIRE_AGREEMENT_CLAUSE = 'entire_agreement_clause',
  AMENDMENT_AND_MODIFICATION_CLAUSE = 'amendment_and_modification_clause',
  DEFAULT_CLAUSE = 'default_clause',
  RIGHT_TO_SELL_CLAUSE = 'right_to_sell_clause',
  RELEASE_CLAUSE = 'release_clause',
  RISK_OF_LOSS_CLAUSE = 'risk_of_loss_clause',
  ESCALATION_CLAUSE = 'escalation_clause',
  RIGHT_OF_FIRST_REFUSAL_CLAUSE = 'right_of_first_refusal_clause',
  CONTENT_LANGUAGE = 'content_language',
  CONTRACT_TYPE = 'contract_type',
  CONTRACT_JURISDICTION = 'contract_jurisdiction',
  DURATION_START_DATE = 'duration_start_date',
  DURATION_TERM = 'duration_term',
  DURATION_END_DATE = 'duration_end_date',
  RENEWAL_DURATION = 'renewal_duration',
  CANCELLATION_NOTICE_PERIOD = 'cancellation_notice_period',
  CANCELLATION_NOTICE_DATE = 'cancellation_notice_date',
  EXPIRATION_DATE = 'expiration_date',
  TERMINATED_DATE = 'terminated_date',
  EFFECTIVE_DATE = 'effective_date',
  SURVIVAL_PERIOD = 'survival_period',
  CONTRACT_VALUE = 'contract_value',
  RECURRING_CONTRACT_VALUE = 'recurring_contract_value',
  NET_RENT = 'net_rent',
  GROSS_RENT = 'gross_rent',
  SECURITY_DEPOSIT = 'security_deposit',
  GROSS_SALARY = 'gross_salary',
  WORK_HOURS = 'work_hours',
  JOB_TITLE = 'job_title',
  VACATION_DAYS = 'vacation_days',
  INVESTMENT_AMOUNT = 'investment_amount',
  VALUATION = 'valuation',
  TAG_ALONG_RIGHTS = 'tag_along_rights',
  DRAG_ALONG_RIGHTS = 'drag_along_rights',
  LIQUIDATION_PREFERENCE = 'liquidation_preference',
  ANTI_DILUTION_PROTECTION = 'anti_dilution_protection',
  BOARD_SEAT = 'board_seat',
  VALUATION_CAP = 'valuation_cap',
  DISCOUNT_RATE = 'discount_rate',
  VALUATION_FLOOR = 'valuation_floor',
  CONTENT_TITLE = 'content_title',
  RELEASE_DATE = 'release_date',
  DATA_PROCESSING_LOCATION = 'data_processing_location',
  DATA_PROCESSING_PURPOSE = 'data_processing_purpose',
  DATA_BREACH_NOTIFICATION_PERIOD = 'data_breach_notification_period',
  DATA_PROCESSING_SUBCONTRACTOR_OBLIGATIONS = 'data_processing_subcontractor_obligations',
  END_USER_LICENSE_TYPE = 'end_user_license_type',
  DEED_OF_TRUST_AMOUNT = 'deed_of_trust_amount',
  REAL_ESTATE_EARNEST_MONEY = 'real_estate_earnest_money',
  STOCK_PURCHASE_QUANTITY = 'stock_purchase_quantity',
  STOCK_PURCHASE_PRICE_PER_SHARE = 'stock_purchase_price_per_share',
  PURCHASE_PRICE = 'purchase_price',
  SERVICE_LOCATION = 'service_location',
  SERVICE_HOURS = 'service_hours',
  SERVICE_RATE = 'service_rate',
  SERVICE_CONTRACT_VALUE = 'service_contract_value',
  RECURRING_SERVICE_CONTRACT_VALUE = 'recurring_service_contract_value',
  LOAN_AMOUNT = 'loan_amount',
  LOAN_INTEREST_RATE = 'loan_interest_rate',
  LOAN_INTEREST_RATE_FREQUENCY = 'loan_interest_rate_frequency',
  LOAN_MATURITY_DATE = 'loan_maturity_date',
  GUARANTEED_AVAILABILITY = 'guaranteed_availability',
  LICENSE_AGREEMENT_VALUE = 'license_agreement_value',
  RECURRING_LICENSE_AGREEMENT_VALUE = 'recurring_license_agreement_value',
  CONSTRUCTION_START_DATE = 'construction_start_date',
  CONSTRUCTION_END_DATE = 'construction_end_date',
  CONSTRUCTION_DURATION = 'construction_duration',
  CONSULTING_HOURS = 'consulting_hours',
  CONSULTING_HOURLY_RATE = 'consulting_hourly_rate',
  CONSULTING_DAILY_RATE = 'consulting_daily_rate',
  FREELANCE_HOURLY_RATE = 'freelance_rate',
  FREELANCE_HOURS = 'freelance_hours',
  VESTING_CLIFF = 'vesting_cliff',
  VESTING_DURATION = 'vesting_duration',
  VESTING_START_DATE = 'vesting_start_date',
  VESTING_END_DATE = 'vesting_end_date',
}
export enum SystemNotificationSettingType {
  EVENT = 'event',
  REMINDER = 'reminder',
  RECURRING = 'recurring',
  SINGLE = 'single',
}
export type TagResource = { uuid: string; name: string; color: string };
export type TaskSnippetResource = {
  uuid: string;
  account_user: AccountUserMinimalResource;
  document_user: DocumentUserSnippetResource;
  document_uuid?: string;
  type: TaskType;
  status: TaskStatus;
  canceled_at?: string;
  resolved_at?: string;
  taskable_type?: string;
  taskable_uuid?: string;
  taskable_name?: string;
  created_at: string;
  updated_at?: string;
};
export enum TaskStatus {
  OPEN = 'open',
  CANCELED = 'canceled',
  EXPIRED = 'expired',
  RESOLVED = 'resolved',
}
export enum TaskType {
  CHECKPOINT_APPROVAL_REQUIRED = 'checkpoint_approval_required',
  SIGNATURE_REQUIRED = 'signature_required',
  CUSTOM_TASK = 'custom_task',
}
export enum Team {
  LEGAL = 'legal',
  OPERATIONS = 'operations',
  SALES = 'sales',
  FINANCE = 'finance',
  PROCUREMENT = 'procurement',
  MARKETING = 'marketing',
  HR = 'hr',
  OTHER = 'other',
}
export type TeamMinimalResource = { uuid: string; name: string; icon: string };
export type TeamResource = {
  uuid: string;
  name: string;
  icon: string;
  created_at: string;
  updated_at: string;
  deleted_at: string;
};
export type TeamSnippetResource = { uuid: string; name: string; icon: string };
export type TeamSnippetWithCountsResource = {
  uuid: string;
  name: string;
  icon: string;
  member_count: number;
  document_count: number;
};
export type TemplateMinimalResource = { uuid: string; name: string; document_type: string };
export type TemplateResource = {
  uuid: string;
  teams: Array<TeamSnippetResource>;
  published: boolean;
  has_counterparties: boolean;
  has_signatories: boolean;
  can_add_internal_signatories: boolean;
  can_change_default_signatories: boolean;
  document_creator_is_default_signatory: boolean;
  name: string;
  locale: string;
  description: string;
  prosemirror_data: Array<any>;
  signature_security_level: SignatureSecurityLevel;
  sequential_signing: boolean;
  document_type: DocumentType;
  archived_at: string;
  global_template_terms_confirmed_at: string;
  is_copied_from_global_template: boolean;
  external_reviewer_invitation_message: string;
  external_signatory_invitation_message: string;
};
export type TemplateSnippetResource = {
  uuid: string;
  teams: Array<TeamSnippetResource>;
  published: boolean;
  name: string;
  document_type: DocumentType;
  tags: Array<TagResource>;
  created_by: CreatedByMinimalResource;
  created_at: string;
  updated_at: string;
  deleted_at: string;
  archived_at: string;
  global_template_terms_confirmed_at: string;
  is_copied_from_global_template: boolean;
  api_integration_settings: Array<ApiIntegrationSettingMinimalResource>;
};
export enum TextMimeType {
  CSV = 'text/csv',
  TXT = 'text/plain',
}
export enum TrackingEventName {
  SIGNED_UP = 'signed_up',
  EMAIL_VERIFIED = 'email_verified',
  ACCOUNT_USER_INVITED = 'account_user_invited',
  ONBOARDING_STARTED = 'onboarding_started',
  ONBOARDING_FINISHED = 'onboarding_finished',
  TEMPLATE_CREATED_FROM_GLOBAL_TEMPLATE = 'template_created_from_global_template',
  TEMPLATE_CREATED_FROM_TEMPLATE = 'template_created_from_template',
  TEMPLATE_CREATED_FROM_SCRATCH = 'template_created_from_scratch',
  TEMPLATE_DYNAMIC_FIELD_CREATED = 'template_dynamic_field_created',
  TEMPLATE_CONDITION_CREATED = 'template_condition_created',
  TEMPLATE_METADATA_ADDED = 'template_metadata_added',
  TEMPLATE_ATTACHMENT_UPLOADED = 'template_attachment_uploaded',
  DOCUMENT_STAGE_CHANGED_REVIEW = 'document_stage_changed_review',
  DOCUMENT_STAGE_CHANGED_DRAFT = 'document_stage_changed_draft',
  DOCUMENT_STAGE_CHANGED_APPROVED_DRAFT = 'document_stage_changed_approved_draft',
  DOCUMENT_STAGE_CHANGED_APPROVED_REVIEW = 'document_stage_changed_approved_review',
  DOCUMENT_STAGE_CHANGED_SIGNING = 'document_stage_changed_signing',
  DOCUMENT_STAGE_CHANGED_SIGNED = 'document_stage_changed_signed',
  DOCUMENT_CREATED_FROM_TEMPLATE = 'document_created_from_template',
  DOCUMENT_CREATED_FROM_SCRATCH = 'document_created_from_scratch',
  DOCUMENT_CREATED_FROM_SIGNED_PDF = 'document_created_from_signed_pdf',
  DOCUMENT_CREATED_FROM_PDF = 'document_created_from_pdf',
  DOCUMENT_WORKING_COPY_CREATED = 'document_working_copy_created',
  DOCUMENT_ATTACHMENT_UPLOADED = 'document_attachment_uploaded',
  DOCUMENT_LINKED_DOCUMENT_ADDED = 'document_linked_document_added',
  DOCUMENT_DOWNLOADED_PDF = 'document_download_pdf',
  DOCUMENT_DOWNLOADED_DOCX = 'document_download_docx',
  DOCUMENT_USER_INVITED = 'document_user_invited',
  DOCUMENT_PROPOSAL_CREATED_INTERNAL = 'document_proposal_created_internal',
  DOCUMENT_PROPOSAL_CREATED_EXTERNAL = 'document_proposal_created_external',
  DOCUMENT_PROPOSAL_RESOLVED = 'document_proposal_resolved',
  DOCUMENT_COMMENT_CREATED_INTERNAL = 'document_comment_created_internal',
  DOCUMENT_COMMENT_CREATED_EXTERNAL = 'document_comment_created_external',
  DOCUMENT_COMMENT_RESOLVED = 'document_comment_resolved',
  DOCUMENT_SIGNATURE_SAVED = 'signature_saved',
  IMPORT_CREATED = 'import_created',
  AI_ANALYSIS_APPLIED = 'ai_analysis_applied',
  VISIT_DOCUMENT_SHOW = 'visit_document_show',
  VISIT_DOCUMENT_LIST = 'visit_document_list',
  VISIT_IMPORT_SHOW = 'visit_import_show',
  VISIT_IMPORT_LIST = 'visit_import_list',
  VISIT_TEMPLATE_LIST = 'visit_template_list',
  VISIT_TEMPLATE_EDIT = 'visit_template_edit',
  VISIT_DASHBOARD_SHOW = 'visit_dashboard_show',
  ACCOUNT_SETTINGS_TAG_ADDED = 'account_settings_tag_added',
  ACCOUNT_SETTINGS_TEAM_ADDED = 'account_settings_team_added',
  ACCOUNT_SETTINGS_ACCOUNT_METADATA_ADDED = 'account_settings_account_metadata_added',
  ACCOUNT_SETTINGS_REFERENCE_METADATA_EDITED = 'account_settings_reference_metadata_edited',
  ACCOUNT_SETTINGS_CONTRACT_TYPE_ADDED = 'account_settings_contract_type_added',
  DASHBOARD_ADDED = 'dashboard_added',
  DASHBOARD_WIDGET_ADDED = 'dashboard_widget_added',
  DASHBOARD_CUSTOM_WIDGET_ADDED = 'dashboard_custom_widget_added',
  TABLE_FILTER_SAVED = 'table_filter_saved',
  TABLE_FILTER_USED = 'table_filter_used',
  TABLE_KANBAN_USED = 'table_kanban_used',
}
export enum TriggerReason {
  BILLING = 'billing',
  SUPERADMIN_ACTION = 'superadmin_action',
  SUSPICIOUS_EMAIL_ACTIVITY = 'suspicious_email_activity',
}
export enum TriggerTimeConditionUnit {
  DAY = 'day',
  WEEK = 'week',
  MONTH = 'month',
  YEAR = 'year',
}
export enum TwoFactorAuthTrustDeviceAction {
  TRUST = 'trust',
  DO_NOT_TRUST = 'do_not_trust',
}
export type UpcomingInvoiceResource = {
  invoices: {
    prorated: {
      amount: number;
      amount_excluding_tax: number;
    };
    upcoming: {
      amount: number;
      amount_excluding_tax: number;
    };
  };
  currency: 'eur' | 'usd';
  period_start: string;
  period_end: string;
};
export type UpdateCustomWidgetRequest = {
  name?: string | null;
  config?: CustomWidgetResource['config'];
};
export type UpdateDashboardRequest = {
  filter?: StoreDashboardRequest['filter'] | null;
  layout?: StoreDashboardRequest['layout'] | null;
  name: StoreDashboardRequest['name'];
  is_default?: StoreDashboardRequest['is_default'];
};
export type UpdateDynamicFieldRequest = Omit<Partial<StoreDynamicFieldRequest>, 'format'> & {
  format?: Partial<StoreDynamicFieldRequest['format']>;
};
export type UpdateExternalApiActorRequest = {
  name?: string;
  roles?: Array<ExternalApiActorRole>;
  expires_at?: string | null;
};
export type UpdateExternalApiSettingRequest = { locked_api_version: string };
export type UpdateNotificationRequest = { is_read: boolean };
export type UserNotificationResource = {
  uuid: string;
  email: string;
  account_user_uuid: string;
  document_user_uuid: string;
  created_at: string;
  message: string;
  notification_type: NotificationType;
  email_log: EmailLogResource | null;
  channels: ('database' | 'mail')[];
};
export enum UserPermission {
  SUPERADMIN_NOVA_ACCESS = 'superadmin_nova_access',
  SUPERADMIN_IMPERSONATE = 'superadmin_impersonate',
  SUPERADMIN_DATA_ACCESS = 'superadmin_data_access',
  SUPERADMIN_DATA_DELETE = 'superadmin_data_delete',
  SUPERADMIN_ACCOUNT_ACCESS = 'superadmin_account_access',
  SUPERADMIN_GLOBAL_TEMPLATE_ACCESS = 'superadmin_global_template_access',
  SUPERADMIN_BILLING_ACCESS = 'superadmin_billing_access',
  SUPERADMIN_STATISTICS_ACCESS = 'superadmin_statistics_access',
  FYNK_INTERNAL_ACCOUNT_ACCESS = 'fynk_internal_account_access',
}
export type UserResource = { uuid: string; email: string };
export enum UserRole {
  REGULAR = 'user-regular',
  SUPERADMIN_CUSTOMER_SUCCESS = 'user-superadmin-customer-success',
  SUPERADMIN_DEVELOPER = 'user-superadmin-developer',
  SUPERADMIN_SALES = 'user-superadmin-sales',
  SUPERADMIN_MARKETING = 'user-superadmin-marketing',
  FYNK_INTERNAL_ACCESS = 'user-fynk-internal-access',
}
export type WorkingCopyResource = { uuid: string; is_published: boolean; created_at: string };
export type _AccountResource = {
  uuid: string;
  name: string;
  ci_logo_uuid: string | null;
  ci_logo_url: string | null;
  ci_colors: { bgColor: string; textColor: string; primaryColor: string; textOnPrimaryColor: string };
  ci_email_signature: string | null;
  trial_ends_at: string | null;
  locale: string;
  created_at: string;
  updated_at: string;
  suspended_at: string | null;
  two_factor_authentication_enabled: boolean;
  status: string;
  is_internal: boolean;
  default_signature_security_level: SignatureSecurityLevel;
  sso_enforced: boolean;
  finished_onboarding_at: string | null;
  advanced_compliance: boolean;
};
